import React from 'react';
import { useApolloClient } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { Flex } from 'deprecated-enkel';
import { GET_ORDERS_OF_TOTEM } from 'data/queries';
import { DAYS_OF_THE_WEEK, WEEKDAY_INFO } from 'constants/date-constants';
import { PAUSED } from 'constants/states-app';
import { TotemButton } from 'ui/Button';
import { PopupTitle } from 'styles/pages/settings.styles';
import { analyticsEvents, analyticsPropertiesConstants, analyticsSourceConstants, track } from 'tracking/segment';
import { OrderFragmentFragment as Order, useReactivateOrderMutation } from 'data/__generated__';
import { OrderTypeSelectionProps } from 'pages/Totem/TotemDashboard/TotemDashboard.types';

export interface UpdateExistingOrderConfirmationProps extends OrderTypeSelectionProps {
    order: Order | null | undefined;
    onCreateNewRecurringOrder?: () => void;
}

export const UpdateExistingOrderConfirmation: React.FC<UpdateExistingOrderConfirmationProps> = ({
    order,
    ordersOfTotem,
    currentTotem,
    defaultCategoryId,
    selectedDate,
    onCreateNewRecurringOrder,
}) => {
    const apolloClient = useApolloClient();
    const navigate = useNavigate();

    const [reactivateOrder] = useReactivateOrderMutation();

    const recentOrders = ordersOfTotem?.recentOrders ?? [];

    // const { _id: totemId } = currentTotem;
    const totemId = currentTotem?._id;

    const weekday: string | undefined = selectedDate
        ? DAYS_OF_THE_WEEK[parseInt(dayjs(selectedDate._d).format('d'), 10)]?.toLowerCase()
        : undefined;
    const translatedWeekday = WEEKDAY_INFO?.find((day) => day?.value === weekday)?.name;

    const createUrl = (orderId: string) => `/totem/${totemId}/${defaultCategoryId}/${orderId}`;

    const handleReactivateOrder = async (orderId: string) => {
        const data = await reactivateOrder({
            variables: { orderId },
        });

        const updatedOrder = data.data?.reactivateOrder;
        const updatedOrders = recentOrders?.map((orderFromCache) =>
            orderFromCache?._id === orderId ? updatedOrder : orderFromCache,
        );

        apolloClient.writeQuery({
            query: GET_ORDERS_OF_TOTEM,
            variables: { totemId },
            data: {
                ordersOfTotem: { ...ordersOfTotem, recentOrders: updatedOrders },
            },
        });
        track(analyticsEvents.REACTIVATE_ORDER, {
            [analyticsPropertiesConstants.COMMON.SOURCE]: analyticsSourceConstants.ORDER_CREATION,
        });

        navigate(createUrl(orderId));
    };

    const handleUpdateOrder = async (orderId: string) => {
        track(analyticsEvents.MODIFY_ORDER, {
            [analyticsPropertiesConstants.COMMON.SOURCE]: analyticsSourceConstants.ORDER_CREATION,
            [analyticsPropertiesConstants.ORDER.IS_PUNCTUAL]: order?.isPunctual,
        });
        navigate(createUrl(orderId));
    };

    const handleCreateNewOrder = () => {
        track(analyticsEvents.CREATE_ORDER, {
            [analyticsPropertiesConstants.COMMON.SOURCE]: analyticsSourceConstants.ORDER_CREATION,
            [analyticsPropertiesConstants.ORDER.IS_PUNCTUAL]: false,
        });
        if (onCreateNewRecurringOrder) {
            onCreateNewRecurringOrder();
        }
    };

    return (
        <>
            <PopupTitle $margins={[1, 0, 1]}>Vous avez déjà une commande prévue {translatedWeekday}</PopupTitle>
            <Flex $flex="1" $justifyContent="center" $direction="column">
                {order?.isPunctual ? (
                    <TotemButton
                        $margins={[1, 3]}
                        style={{ color: 'yellow' }}
                        onClick={() => {
                            handleUpdateOrder(order._id);
                        }}
                    >
                        Modifier ma commande
                    </TotemButton>
                ) : order?.state === PAUSED ? (
                    <>
                        <TotemButton
                            $margins={[1, 3]}
                            style={{ color: 'yellow' }}
                            onClick={() => handleReactivateOrder(order._id)}
                            data-test="reactivate-order-button"
                        >
                            Réactiver ma commande en pause
                        </TotemButton>
                        <TotemButton
                            $type="secondary"
                            $margins={[1, 3]}
                            onClick={handleCreateNewOrder}
                            data-test="create-additional-order-button"
                        >
                            Créer une nouvelle commande récurrente
                        </TotemButton>
                    </>
                ) : (
                    <>
                        <TotemButton
                            style={{ color: 'yellow' }}
                            $margins={[0, 3]}
                            data-test="adjust-recurrent-order-button"
                            onClick={() => {
                                if (order?._id) handleUpdateOrder(order._id);
                            }}
                            disabled={!order?._id}
                        >
                            Modifier ma commande récurrente
                        </TotemButton>
                        <TotemButton
                            $type="secondary"
                            $margins={[1, 3]}
                            onClick={handleCreateNewOrder}
                            data-test="create-additional-order-button"
                        >
                            Créer une nouvelle commande récurrente
                        </TotemButton>
                    </>
                )}
            </Flex>
        </>
    );
};
